/**
 * Which front end serves /_/chats: the messenger built into this site («native») or the old
 * standalone Hami site in an iframe («legacy»). The native one is for the new Android app
 * (see common/utils/newAppShell); the website and older app builds keep the iframe.
 */
export type HamiFront = 'pending' | 'native' | 'legacy';

interface Input {
  /** ?native=1 / ?native=0 on the URL (QA), or the build-time switch for local dev. */
  force: boolean | null;
  /** null until the client has read the <html> flag set by the new app's head script. */
  newApp: boolean | null;
}

export const decideHamiFront = ({ force, newApp }: Input): HamiFront => {
  if (force !== null) return force ? 'native' : 'legacy';
  if (newApp === null) return 'pending';
  return newApp ? 'native' : 'legacy';
};

export const parseForce = (query: unknown, buildSwitch: string | undefined): boolean | null => {
  const raw = Array.isArray(query) ? query[0] : query;
  if (raw === '1') return true;
  if (raw === '0') return false;
  return buildSwitch === '1' ? true : null;
};

/**
 * Browser only, before React: will this page show the native messenger? Same answer as `decideHamiFront`, from the
 * <html> flag the new app's head script has already set and from ?native= / ?legacy= / the build switch.
 */
export const willUseNativeFront = (): boolean => {
  if (typeof document === 'undefined' || typeof window === 'undefined') return false;
  const params = new URLSearchParams(window.location.search);
  if (params.get('legacy') === '1') return false;
  const force = parseForce(params.get('native') ?? undefined, process.env.NEXT_PUBLIC_HAMI_NATIVE_FRONT);
  if (force !== null) return force;
  return document.documentElement.hasAttribute('data-floating-bottom-nav');
};
